import React, { useRef, useState } from 'react';
import { PulseLoader } from 'react-spinners';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { DataTable } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';

import { useListUnitysController } from 'src/controllers/Unitys/ListUnitysController';
import { useFormCreateUnityPeoples } from 'src/controllers/People/CreateUnityPeopleController';
import { useListUnityPeoplesController } from 'src/controllers/People/ListUnityPeopleController';
import useIntersectionObserver from 'src/components/IntersectionObserver/useIntersectionObserver';

import { ModelOfPeople } from 'src/database';
import { dateToString } from 'src/utils/dateToString';
import LoadingScreen from 'src/components/LoadingScreen';

interface filteredProps {
    label: string;
    value?: string;
};

const UnityPeoplesList: React.FC = () => {
    const { getUnityPeopleListController, handleUnityPeopleListController } = useListUnityPeoplesController();
    const { getUnityPeopleFormController } = useFormCreateUnityPeoples();
    const { getUnityListController } = useListUnitysController();

    const [globalFilter, setGlobalFilter] = useState<string | null>(null);
    const [valueUnity, setValueUnity] = useState('');
    const [filteredUnity, setFilteredUnity] = useState<filteredProps[]>([{ label: '', value: '' }]);
    const [valueRole, setValueRole] = useState('');
    const [filteredRole, setFilteredRole] = useState<filteredProps[]>([{ label: '', value: '' }]);

    if (getUnityPeopleListController.loading) {
        return <LoadingScreen />
    };

    const unitys = getUnityListController.AllUnitysData.map((unity) => {
        return { label: unity.NOME, value: unity.ID }
    });

    const roles = getUnityPeopleFormController.roles.map((role) => {
        return { label: role.DESCRICAO, value: role.ID }
    });

    const searchUnitys = (event: any) => {
        let filteredUnitys;
        if (!event.query.trim().length) {
            filteredUnitys = [...unitys];
        }
        else {
            filteredUnitys = unitys.filter((unity) => {
                return unity.label.toLowerCase().includes(event.query.toLowerCase());
            });
        }
        setFilteredUnity(filteredUnitys);
    };

    const searchRoles = (event: any) => {
        let filteredRoles;
        if (!event.query.trim().length) {
            filteredRoles = [...roles];
        }
        else {
            filteredRoles = roles.filter((role) => {
                return role.label.toLowerCase().includes(event.query.toLowerCase());
            });
        }
        setFilteredRole(filteredRoles);
    };

    const tableHeader = (
        <div className="table-header p-grid p-d-flex p-jc-between">
            <h5 className="p-m-0">Pessoas</h5>
            <span className="p-input-icon-left p-search-bar">
                <i className="pi pi-search" />
                <InputText type="search" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGlobalFilter(e.target.value)} placeholder="Buscar..." />
            </span>
        </div>
    );

    const actionBodyTemplate = (UnityPeople: ModelOfPeople) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-success" onClick={() => handleUnityPeopleListController.openEditDialog(UnityPeople)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger" onClick={() => handleUnityPeopleListController.openDeleteDialog(UnityPeople)} />
            </React.Fragment>
        );
    };

    const editUnityPeopleDialogFooter = (
        <React.Fragment>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={handleUnityPeopleListController.closeEditDialog} />
            <Button type="submit" label="Salvar" icon="pi pi-check" className="p-button-text" onClick={() => { getUnityPeopleListController.formik.handleSubmit() }} />
        </React.Fragment>
    );

    const deleteUnityPeopleDialogFooter = (
        <React.Fragment>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={handleUnityPeopleListController.closeDeleteDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text"
                onClick={() => {
                    getUnityPeopleListController.selectedUnityPeople && handleUnityPeopleListController.removeUnityPeople(getUnityPeopleListController.selectedUnityPeople)
                }}
            />
        </React.Fragment>
    );

    const leftUnityPeopleToolbar = () => {
        return (
            <Link to="/peopleForm">
                <Button label="Cadastrar pessoa" icon="pi pi-plus" className="p-button-success p-mr-2" />
            </Link>
        );
    };

    const HEADER_NOME = (rowData: ModelOfPeople) => {
        return (
            <React.Fragment>
                <span className="left-header">NOME</span>
                {rowData.NOME}
            </React.Fragment>
        );
    }

    const HEADER_CARGO = (rowData: ModelOfPeople) => {
        return (
            <React.Fragment>
                <span className="left-header">CARGO</span>
                {rowData.DESCRICAO_CARGO}
            </React.Fragment>
        );
    }

    const HEADER_DATANASCIMENTO = (rowData: ModelOfPeople) => {
        return (
            <React.Fragment>
                <span className="left-header">DATA DE NASCIMENTO</span>
                {dateToString(rowData.DATANASCIMENTO, 'br')}
            </React.Fragment>
        );
    }

    const HEADER_CPF = (rowData: ModelOfPeople) => {
        return (
            <React.Fragment>
                <span className="left-header">CPF</span>
                {rowData.CPF}
            </React.Fragment>
        );
    }

    const HEADER_ORGAOUNIDADE = (rowData: ModelOfPeople) => {
        return (
            <React.Fragment>
                <span className="left-header">UNIDADE</span>
                {rowData.NOME_ORGAOUNIDADE}
            </React.Fragment>
        );
    }

    const Loader: React.FC = ({ children }) => {
        const divRef: any = useRef<HTMLDivElement | null>(null);

        const [isVisible] = useIntersectionObserver({
            elementRef: divRef,
        });

        if (isVisible && !getUnityPeopleListController.maxData && !getUnityPeopleListController.loadingRows) {
            handleUnityPeopleListController.setRows(getUnityPeopleListController.rows + 20);
        };

        return (
            <div
                ref={divRef}
                style={{
                    minHeight: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {children}
            </div>
        )
    }

    return (
        <div>
            <Toast ref={getUnityPeopleListController.toast} />
            <div className="p-card p-p-4 datatable-container">
                <Toolbar className="p-mb-4" left={leftUnityPeopleToolbar}></Toolbar>

                <DataTable
                    header={tableHeader}
                    globalFilter={globalFilter} rowHover
                    className="responsive-table p-datatable-sm"
                    dataKey="ID" autoLayout scrollHeight="100%" scrollable
                    value={getUnityPeopleListController.unityPeoplesData}
                >
                    <Column headerStyle={{ width: '300px' }} field="NOME" header="NOME" body={HEADER_NOME}></Column>
                    <Column field="CARGO" header="CARGO" body={HEADER_CARGO}></Column>
                    <Column field="DATANASCIMENTO" header="NASCIMENTO" body={HEADER_DATANASCIMENTO}></Column>
                    <Column field="CPF" header="CPF" body={HEADER_CPF}></Column>
                    <Column field="ORGAOUNIDADE" header="UNIDADE" body={HEADER_ORGAOUNIDADE}></Column>
                    <Column body={actionBodyTemplate} bodyStyle={{ textAlign: 'right' }}></Column>
                </DataTable>
                <Loader>{!getUnityPeopleListController.maxData ?
                    getUnityPeopleListController.loadingRows ? <PulseLoader color="#0388e5" size={8} /> : <></>
                    : "Lista completa"}
                </Loader>
            </div>

            <Dialog
                className="p-fluid dropdown-visible"
                style={{ width: '90%' }}
                visible={getUnityPeopleListController.editDialog}
                onHide={handleUnityPeopleListController.closeEditDialog}
                onShow={() => {
                    getUnityPeopleListController.formik.setValues({
                        ...getUnityPeopleListController.selectedUnityPeople,
                        DATANASCIMENTO: (dateToString(getUnityPeopleListController.selectedUnityPeople.DATANASCIMENTO, 'br'))
                    });
                    setValueRole(getUnityPeopleListController.selectedUnityPeople.DESCRICAO_CARGO || '');
                    setValueUnity(getUnityPeopleListController.selectedUnityPeople.NOME_ORGAOUNIDADE || '');
                }}
                footer={editUnityPeopleDialogFooter}
                header={`Editar pessoa ${getUnityPeopleListController.selectedUnityPeople?.NOME}`}
                modal
            >
                {getUnityPeopleListController.loadingEdit ? <LoadingScreen color="#0388e5" /> : <></>}
                <div className="p-grid p-fluid">
                    <div className="p-field p-col-12 p-md-2">
                        <label htmlFor="orgao" >CPF</label>
                        <InputMask id="cpf" disabled
                            mask="999.999.999-99"
                            {...getUnityPeopleListController.formik.getFieldProps('CPF')}
                        />
                        <div className={classNames("p-invalid popup-fade", {
                            'popup-form-active': getUnityPeopleListController.formik.touched.CPF && getUnityPeopleListController.formik.errors.CPF,
                            'popup-form-inactive': !(getUnityPeopleListController.formik.touched.CPF && getUnityPeopleListController.formik.errors.CPF),
                        })}
                        >{getUnityPeopleListController.formik.errors.CPF}</div>
                    </div>

                    <div className="p-field p-col-12 p-md-8">
                        <label htmlFor="nome" >Nome</label>
                        <InputText id="nome"
                            {...getUnityPeopleListController.formik.getFieldProps('NOME')}
                            onChange={getUnityPeopleListController.formik.handleChange('NOME')}
                        />
                        <div className={classNames("p-invalid popup-fade", {
                            'popup-form-active': getUnityPeopleListController.formik.touched.NOME && getUnityPeopleListController.formik.errors.NOME,
                            'popup-form-inactive': !(getUnityPeopleListController.formik.touched.NOME && getUnityPeopleListController.formik.errors.NOME),
                        })}
                        >{getUnityPeopleListController.formik.errors.NOME}</div>
                    </div>

                    <div className="p-field p-col-12 p-md-2">
                        <label htmlFor="email" >Data de nascimento</label>
                        <InputMask
                            mask="99/99/9999" placeholder="__/__/____"
                            {...getUnityPeopleListController.formik.getFieldProps('DATANASCIMENTO')}
                            onChange={(e) => getUnityPeopleListController.formik.setFieldValue('DATANASCIMENTO', e.value)}
                        />
                        <div className={classNames("p-invalid popup-fade", {
                            'popup-form-active': getUnityPeopleListController.formik.touched.DATANASCIMENTO && getUnityPeopleListController.formik.errors.DATANASCIMENTO,
                            'popup-form-inactive': !(getUnityPeopleListController.formik.touched.DATANASCIMENTO && getUnityPeopleListController.formik.errors.DATANASCIMENTO),
                        })}
                        >{getUnityPeopleListController.formik.errors.DATANASCIMENTO}</div>
                    </div>

                    <div className="p-field p-col-12 p-md-4">
                        <label htmlFor="telefone" >Cargo</label>
                        <AutoComplete id="cargo"
                            value={valueRole}
                            field="label" placeholder="Buscar..."
                            onChange={(e) => {
                                setValueRole(e.value)
                                getUnityPeopleListController.formik.setFieldValue('CARGO', filteredRole.find((role) => {
                                    return e.value === role.label
                                })?.value || '')
                            }}
                            onSelect={(e) => {
                                setValueRole(e.value.label)
                                getUnityPeopleListController.formik.setFieldValue('CARGO', e.value.value)
                            }}
                            suggestions={filteredRole} completeMethod={searchRoles}
                            dropdown
                        />
                        <div className={classNames("p-invalid popup-fade", {
                            'popup-form-active': getUnityPeopleListController.formik.touched.CARGO && getUnityPeopleListController.formik.errors.CARGO,
                            'popup-form-inactive': !(getUnityPeopleListController.formik.touched.CARGO && getUnityPeopleListController.formik.errors.CARGO),
                        })}
                        >{getUnityPeopleListController.formik.errors.CARGO}</div>
                    </div>

                    <div className="p-field p-col-12 p-md-8">
                        <label htmlFor="telefone" >Unidade</label>
                        <AutoComplete id="unidade"
                            value={valueUnity}
                            field="label" placeholder="Buscar..."
                            onChange={(e) => {
                                setValueUnity(e.value)
                                getUnityPeopleListController.formik.setFieldValue('ORGAOUNIDADE', filteredUnity.find((unity) => {
                                    return e.value === unity.label
                                })?.value || '')
                            }}
                            onSelect={(e) => {
                                setValueUnity(e.value.label)
                                getUnityPeopleListController.formik.setFieldValue('ORGAOUNIDADE', e.value.value)
                            }}
                            suggestions={filteredUnity} completeMethod={searchUnitys}
                            dropdown
                        />
                        <div className={classNames("p-invalid popup-fade", {
                            'popup-form-active': getUnityPeopleListController.formik.touched.ORGAOUNIDADE && getUnityPeopleListController.formik.errors.ORGAOUNIDADE,
                            'popup-form-inactive': !(getUnityPeopleListController.formik.touched.ORGAOUNIDADE && getUnityPeopleListController.formik.errors.ORGAOUNIDADE),
                        })}
                        >{getUnityPeopleListController.formik.errors.ORGAOUNIDADE}</div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                style={{ width: '450px' }}
                visible={getUnityPeopleListController.deleteDialog}
                onHide={handleUnityPeopleListController.closeDeleteDialog}
                footer={deleteUnityPeopleDialogFooter}
                header="Remover pessoa"
                modal
            >
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle p-mr-3" style={{ fontSize: '2rem' }} />
                    {getUnityPeopleListController.selectedUnityPeople && <span>Você tem certeza que deseja deletar <b>{getUnityPeopleListController.selectedUnityPeople.NOME}</b>?</span>}
                </div>
            </Dialog>
        </div>

    );
};

export default UnityPeoplesList;